import { useQuery } from '@tanstack/react-query'
import { api } from './client'
import type { Order, PlaceOrderRequest, Shop, StorefrontCategory } from '../types'

export async function fetchShop(): Promise<Shop> {
  const { data } = await api.get<Shop>('/storefront/shop')
  return data
}

export async function fetchCatalog(): Promise<StorefrontCategory[]> {
  const { data } = await api.get<StorefrontCategory[]>('/storefront/catalog')
  return data
}

/** Shop branding rarely changes within a visit — keep it fresh for 5 minutes. */
export function useShop() {
  return useQuery({ queryKey: ['shop'], queryFn: fetchShop, staleTime: 5 * 60 * 1000 })
}

export function useCatalog() {
  return useQuery({ queryKey: ['catalog'], queryFn: fetchCatalog, staleTime: 60 * 1000 })
}

/** Place an order (needs a customer login). The server re-prices from the catalog and returns the order + tracking token. */
export async function placeOrder(payload: PlaceOrderRequest): Promise<Order> {
  const { data } = await api.post<Order>('/storefront/orders', payload)
  return data
}

/** Track an order by its unguessable token (public). */
export async function fetchOrder(token: string): Promise<Order> {
  const { data } = await api.get<Order>(`/storefront/orders/track/${token}`)
  return data
}

export function useTrackOrder(token: string | undefined) {
  return useQuery({
    queryKey: ['order', token],
    queryFn: () => fetchOrder(token as string),
    enabled: !!token,
    // Live status: poll every 5s while the order is still in flight, stop once it's terminal.
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'DELIVERED' || s === 'CANCELLED' ? false : 5000
    },
  })
}
